import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity, TouchableWithoutFeedback, Switch, TextInput as RNTextInput, Dimensions } from 'react-native';
import { Text, Button, Card, TextInput, List, Divider, ActivityIndicator, IconButton, Modal, Portal, RadioButton, Checkbox, Menu } from 'react-native-paper';

// Input numérico editable con commit al blur. Mantiene un draft local para
// permitir borrar y retipear sin que el valor controlado lo revierta a media
// edición.
const EditableQuantity = ({ value, onCommit, style }) => {
  const [draft, setDraft] = React.useState(String(value));

  React.useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const n = parseInt(draft, 10);
    if (!isNaN(n) && n > 0 && n !== value) {
      onCommit(n);
    }
    // Resync con la prop. Si onCommit fue aceptado, el useEffect arriba
    // ya actualizará al nuevo value en el siguiente render. Si fue rechazado
    // (p. ej. por stock insuficiente), esto restaura el valor anterior.
    setDraft(String(value));
  };

  return (
    <RNTextInput
      value={draft}
      onChangeText={(t) => setDraft(t.replace(/[^0-9]/g, ''))}
      onBlur={commit}
      onSubmitEditing={commit}
      keyboardType="number-pad"
      returnKeyType="done"
      selectTextOnFocus
      style={style}
    />
  );
};

// Input de precio editable (permite decimales) con commit al blur. Igual que
// EditableQuantity pero acepta punto decimal y valores >= 0. Se usa para poder
// ajustar el precio unitario directamente desde el carrito de la venta.
const EditablePrice = ({ value, onCommit, style }) => {
  const [draft, setDraft] = React.useState(String(value));

  React.useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const n = parseFloat(draft);
    if (!isNaN(n) && n >= 0 && n !== Number(value)) {
      onCommit(n);
    }
    setDraft(String(value));
  };

  return (
    <RNTextInput
      value={draft}
      onChangeText={(t) => setDraft(t.replace(/[^0-9.]/g, ''))}
      onBlur={commit}
      onSubmitEditing={commit}
      keyboardType="decimal-pad"
      returnKeyType="done"
      selectTextOnFocus
      style={style}
    />
  );
};
import { useNavigation, useRoute } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import AsyncStorage from '@react-native-async-storage/async-storage';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { generateFacturaHTML as genFactura, generateTicketHTML as genTicket } from '../../utils/facturaGenerator';

const NuevaVentaScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  const clienteSeleccionado = route.params?.cliente;
  // El vendedor NO puede modificar el precio en la venta (control de precios).
  // Solo admin/supervisor/gerente pueden editarlo.
  const puedeEditarPrecio = ['admin', 'supervisor', 'gerente'].includes(user?.rol);

  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [productoSearch, setProductoSearch] = useState('');
  const [showClientesList, setShowClientesList] = useState(false);
  const [showProductosList, setShowProductosList] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState(null);
  // Multi-select del listado de productos: marcar varios y agregarlos al carrito
  // de una sola en lugar de hacerlo uno por uno.
  const [productosMultiSelect, setProductosMultiSelect] = useState(false);
  const [productosSeleccionadosIds, setProductosSeleccionadosIds] = useState(new Set());
  
  const [venta, setVenta] = useState({
    cliente: clienteSeleccionado || null,
    items: [],
    fecha: new Date().toISOString(),
    subtotal: 0,
    descuento_tipo: 'porcentaje',
    descuento_valor: 0,
    descuento_monto: 0,
    total: 0,
    metodo_pago: 'efectivo',
    dias_credito: 8,
    venta_directa: false,
    notas: '',
  });

  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [facturaData, setFacturaData] = useState(null);
  const [printSize, setPrintSize] = useState('factura'); // 'factura' o 'ticket'
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [lotesProducto, setLotesProducto] = useState([]);
  const [presentacionMenuIndex, setPresentacionMenuIndex] = useState(null);

  // Borradores: si esta pantalla está editando un borrador (retomado), guarda
  // el id; al "Guardar Venta" definitivo se procesa ese mismo registro en BD
  // en lugar de crear uno nuevo.
  const [borradorId, setBorradorId] = useState(null);
  // Flag que evita disparar el diálogo de "Guardar borrador" cuando ya
  // procesamos/cancelamos explícitamente y solo queremos salir.
  const [salirSinPreguntar, setSalirSinPreguntar] = useState(false);
  const [chequeoBorradoresHecho, setChequeoBorradoresHecho] = useState(false);
  const [showLoteModal, setShowLoteModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('Cargando clientes y productos...');
        
        // Cargar clientes
        const clientesData = await api.getClientes();
        console.log('Clientes cargados:', JSON.stringify(clientesData.slice(0, 2), null, 2));
        setClientes(clientesData);
        
        // Cargar productos
        const productosData = await api.getProductos();
        console.log('Estructura de productos:', JSON.stringify(productosData[0]));
        setProductos(productosData);
        
        console.log(`Total clientes cargados: ${clientesData.length}`);
        console.log(`Total productos cargados: ${productosData.length}`);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        Alert.alert('Error', 'No se pudieron cargar los datos necesarios');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // ===== BORRADORES =====
  // Mapea una venta-borrador del backend (con detalles) al state local.
  const aplicarBorrador = (borradorVenta, productosCargados) => {
    if (!borradorVenta) return;
    const productosLocales = productosCargados || productos;
    const detalles = borradorVenta.items || borradorVenta.detalles || [];
    const itemsLocal = detalles.map((d) => {
      const productoLocal = productosLocales.find(p => p.id === d.producto_id);
      const producto = productoLocal || {
        id: d.producto_id,
        nombre: d.producto_nombre || `Producto ${d.producto_id}`,
        precio_venta: d.precio_unitario,
        stock_total: 0,
        presentaciones: [],
      };
      const factor = Number(d.factor_conversion) > 0 ? Number(d.factor_conversion) : 1;
      // BD guarda en unidades base — convertir a presentación para UI.
      const cantPres = factor > 1 ? Math.round((Number(d.cantidad) || 0) / factor) : Number(d.cantidad) || 0;
      const precioPres = factor > 1
        ? Math.round((Number(d.precio_unitario) || 0) * factor * 100) / 100
        : Number(d.precio_unitario) || 0;
      const bruto = Math.round(precioPres * cantPres * 100) / 100;
      return {
        producto,
        cantidad: cantPres,
        precio_unitario: precioPres,
        subtotal_bruto: bruto,
        descuento_tipo: d.descuento_tipo || 'porcentaje',
        descuento_valor: Number(d.descuento_valor) || 0,
        descuento_monto: Number(d.descuento_monto) || 0,
        subtotal: Number(d.subtotal) || bruto,
        lote_id: d.lote_id || null,
        lote_numero: d.lote_numero || null,
        presentacion_id: d.unidad_id || d.presentacion_id || null,
        factor_conversion: factor,
        unidad_nombre: d.unidad_nombre || 'Unidad',
      };
    });

    const subtotal = itemsLocal.reduce((s, it) => s + (it.subtotal || 0), 0);
    const dMonto = Number(borradorVenta.descuento_monto) || 0;
    const total = Math.round((subtotal - dMonto) * 100) / 100;

    setVenta({
      cliente: borradorVenta.cliente_id ? {
        id: borradorVenta.cliente_id,
        nombre: borradorVenta.cliente_nombre || 'Cliente',
        telefono: borradorVenta.cliente_telefono,
        direccion: borradorVenta.cliente_direccion,
      } : null,
      items: itemsLocal,
      fecha: borradorVenta.fecha || new Date().toISOString(),
      subtotal,
      descuento_tipo: borradorVenta.descuento_tipo || 'porcentaje',
      descuento_valor: Number(borradorVenta.descuento_valor) || 0,
      descuento_monto: dMonto,
      total,
      metodo_pago: borradorVenta.metodo_pago || 'efectivo',
      dias_credito: Number(borradorVenta.dias_credito) || 8,
      venta_directa: !!borradorVenta.venta_directa,
      notas: borradorVenta.notas || '',
    });
    setBorradorId(borradorVenta.id);
  };

  // Al entrar a la pantalla: si trae un borrador_id en params, retomarlo;
  // si no, chequear si el usuario tiene borradores y ofrecer retomar.
  useEffect(() => {
    if (productos.length === 0) return;
    if (chequeoBorradoresHecho) return;
    setChequeoBorradoresHecho(true);

    const paramBorradorId = route.params?.borrador_id;

    const cargarPorParams = async () => {
      try {
        const data = await api.getVenta(paramBorradorId);
        aplicarBorrador(data, productos);
      } catch (e) {
        console.error('Error al cargar borrador:', e);
        Alert.alert('Error', 'No se pudo cargar el borrador');
      }
    };

    const ofrecerRetomar = async () => {
      try {
        const lista = await api.getBorradoresVenta();
        if (!Array.isArray(lista) || lista.length === 0) return;
        // El más reciente (GET ya viene ordenado DESC por fecha)
        const masReciente = lista[0];
        Alert.alert(
          'Tienes borradores pendientes',
          `Tienes ${lista.length} borrador${lista.length === 1 ? '' : 'es'} sin procesar. ¿Retomar el más reciente?`,
          [
            { text: 'Empezar nueva', style: 'cancel' },
            {
              text: 'Retomar último',
              onPress: async () => {
                try {
                  const data = await api.getVenta(masReciente.id);
                  aplicarBorrador(data, productos);
                } catch (e) {
                  Alert.alert('Error', 'No se pudo retomar el borrador');
                }
              },
            },
          ]
        );
      } catch (e) {
        console.error('Error al chequear borradores:', e);
      }
    };

    if (paramBorradorId) cargarPorParams();
    else ofrecerRetomar();
  }, [productos, chequeoBorradoresHecho, route.params?.borrador_id]);

  // Construye el payload de items para POST/PUT al backend.
  // Convierte cantidad/precio de la presentación a unidades base.
  const itemsAPayload = () => venta.items.map(item => {
    const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
    return {
      producto_id: item.producto.id,
      cantidad: item.cantidad * factor,
      precio_unitario: Math.round((item.precio_unitario / factor) * 100) / 100,
      lote_id: item.lote_id || null,
      lote_numero: item.lote_numero || null,
      descuento_tipo: item.descuento_valor > 0 ? item.descuento_tipo : null,
      descuento_valor: Number(item.descuento_valor) || 0,
      presentacion_id: item.presentacion_id || null,
      factor_conversion: factor,
      unidad_nombre: item.unidad_nombre || 'Unidad',
    };
  });

  // Guarda como borrador (crea o actualiza). Devuelve el id del borrador.
  const guardarBorrador = async () => {
    const userStr = await AsyncStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user?.id) throw new Error('Sesión expirada');
    if (!venta.cliente) throw new Error('Necesita cliente para guardar borrador');

    const payload = {
      cliente_id: venta.cliente.id,
      usuario_id: user.id,
      items: itemsAPayload(),
      descuento_tipo: venta.descuento_valor > 0 ? venta.descuento_tipo : null,
      descuento_valor: Number(venta.descuento_valor) || 0,
      metodo_pago: venta.metodo_pago,
      dias_credito: venta.metodo_pago === 'credito' ? venta.dias_credito : 0,
      venta_directa: !!venta.venta_directa,
      notas: venta.notas,
    };

    if (borradorId) {
      await api.actualizarBorradorVenta(borradorId, payload);
      return borradorId;
    }
    const res = await api.guardarBorradorVenta(payload);
    if (res?.id) setBorradorId(res.id);
    return res?.id;
  };

  // Listener: al salir sin procesar, ofrecer guardar borrador o descartar.
  useEffect(() => {
    const unsub = navigation.addListener('beforeRemove', (e) => {
      if (salirSinPreguntar) return;
      const tieneDatos = !!venta.cliente || venta.items.length > 0;
      if (!tieneDatos) return;

      e.preventDefault();
      Alert.alert(
        'Venta sin procesar',
        venta.cliente
          ? '¿Guardar como borrador para retomar luego o descartar?'
          : 'No hay cliente seleccionado y no se puede guardar como borrador. ¿Descartar los cambios?',
        venta.cliente
          ? [
              { text: 'Seguir editando', style: 'cancel', onPress: () => {} },
              {
                text: 'Descartar',
                style: 'destructive',
                onPress: async () => {
                  if (borradorId) {
                    try { await api.eliminarBorradorVenta(borradorId); } catch (err) {}
                  }
                  setSalirSinPreguntar(true);
                  navigation.dispatch(e.data.action);
                },
              },
              {
                text: 'Guardar borrador',
                onPress: async () => {
                  try {
                    await guardarBorrador();
                  } catch (err) {
                    Alert.alert('No se guardó', err.message || 'Error al guardar borrador');
                    return;
                  }
                  setSalirSinPreguntar(true);
                  navigation.dispatch(e.data.action);
                },
              },
            ]
          : [
              { text: 'Seguir editando', style: 'cancel', onPress: () => {} },
              {
                text: 'Descartar',
                style: 'destructive',
                onPress: () => {
                  setSalirSinPreguntar(true);
                  navigation.dispatch(e.data.action);
                },
              },
            ]
      );
    });
    return unsub;
  }, [navigation, venta, borradorId, salirSinPreguntar]);

  const filteredClientes = clienteSearch === ''
    ? clientes.slice(0, 10) // Mostrar solo los primeros 10 clientes cuando no hay búsqueda
    : clientes.filter(c =>
        (c.nombre || '').toLowerCase().includes(clienteSearch.toLowerCase()) ||
        (c.telefono || '').includes(clienteSearch) ||
        (c.direccion || '').toLowerCase().includes(clienteSearch.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(clienteSearch.toLowerCase())
      ).slice(0, 20); // Limitar a 20 resultados

  const filteredProductos = productoSearch === '' 
    ? productos 
    : productos.filter(p =>
        (p.nombre || '').toLowerCase().includes(productoSearch.toLowerCase()) ||
        (p.id || '').toString().includes(productoSearch)
      );

  const handleSelectCliente = (cliente) => {
    console.log('=== INICIO SELECCIÓN CLIENTE ===');
    console.log('Cliente a seleccionar:', JSON.stringify(cliente, null, 2));
    console.log('Estado de venta ANTES:', JSON.stringify(venta, null, 2));
    
    // Crear nueva venta con cliente
    const nuevaVenta = {
      ...venta,
      cliente: cliente
    };
    
    console.log('Estado de venta DESPUÉS:', JSON.stringify(nuevaVenta, null, 2));
    
    // Actualizar estado
    setVenta(nuevaVenta);
    
    // Cerrar lista inmediatamente
    setShowClientesList(false);
    setClienteSearch('');
    
    console.log('=== FIN SELECCIÓN CLIENTE ===');
    
    // Verificar que el estado se actualizó después de un momento
    setTimeout(() => {
      console.log('Estado final verificado:', JSON.stringify(venta, null, 2));
    }, 100);
  };

  const handleClienteSearchFocus = () => {
    setShowClientesList(true);
  };

  const handleClienteSearchBlur = () => {
    // Usar un timeout más largo para permitir la selección
    setTimeout(() => {
      setShowClientesList(false);
    }, 500);
  };

  const handleAddProducto = async (producto) => {
    setShowProductosList(false);
    setProductoSearch(producto.nombre);
    setSelectedProducto(producto);

    // Cargar lotes del producto. Si hay activos, dejar que el usuario elija;
    // si no, agregar directo al carrito.
    try {
      const lotes = await api.getLotesProducto(producto.id);
      const lotesActivos = Array.isArray(lotes)
        ? lotes.filter(l => l.estado === 'activo' && l.cantidad_actual > 0)
        : [];
      setLotesProducto(lotesActivos);
      if (lotesActivos.length > 0) {
        setShowLoteModal(true);
        return;
      }
    } catch (e) {
      setLotesProducto([]);
    }

    addItemToCart(producto);
  };

  const handleRemoveProducto = (index) => {
    const updatedItems = [...venta.items];
    updatedItems.splice(index, 1);
    updateVentaWithItems(updatedItems);
  };

  // Calcula el descuento en dinero a partir de tipo + valor sobre una base.
  // Igual al helper del backend para que UI y servidor concuerden.
  const calcDescuentoMonto = (tipo, valor, base) => {
    const baseNum = Number(base) || 0;
    const valNum = Number(valor) || 0;
    if (!tipo || valNum <= 0 || baseNum <= 0) return 0;
    let monto = 0;
    if (tipo === 'porcentaje') {
      const pct = Math.min(Math.max(valNum, 0), 100);
      monto = baseNum * (pct / 100);
    } else if (tipo === 'monto') {
      monto = valNum;
    }
    return Math.round(Math.min(monto, baseNum) * 100) / 100;
  };

  // Recalcula subtotal de línea (precio * cantidad - descuento_linea) y
  // todo el carrito (subtotal de venta + descuento global -> total).
  const recalcItem = (item) => {
    const bruto = Math.round((item.precio_unitario * item.cantidad) * 100) / 100;
    const descMonto = calcDescuentoMonto(item.descuento_tipo, item.descuento_valor, bruto);
    return {
      ...item,
      subtotal_bruto: bruto,
      descuento_monto: descMonto,
      subtotal: Math.round((bruto - descMonto) * 100) / 100,
    };
  };

  const recalcVenta = (items, descGlobalTipo, descGlobalValor) => {
    const subtotal = Math.round(items.reduce((s, it) => s + (it.subtotal || 0), 0) * 100) / 100;
    const descGlobalMonto = calcDescuentoMonto(descGlobalTipo, descGlobalValor, subtotal);
    const total = Math.round((subtotal - descGlobalMonto) * 100) / 100;
    return { subtotal, descuento_monto: descGlobalMonto, total };
  };

  // Plan de cuotas según el plazo (debe coincidir con backend/_cuotasHelper.js):
  //   8 -> [8], 15 -> [8,15], 22 -> [8,15,22].
  // Devuelve [{numero, monto, fecha}] con fecha formateada para mostrar.
  const generarPlanCuotas = (total, diasCredito) => {
    let plan;
    if (diasCredito === 22) plan = [8, 15, 22];
    else if (diasCredito === 15) plan = [8, 15];
    else plan = [diasCredito || 8];

    const totalCent = Math.round((Number(total) || 0) * 100);
    const n = plan.length;
    const cuotaCent = Math.floor(totalCent / n);
    const resto = totalCent - cuotaCent * n;
    const hoy = new Date();

    return plan.map((dias, idx) => {
      const fecha = new Date(hoy);
      fecha.setDate(fecha.getDate() + dias);
      const monto = (cuotaCent + (idx === n - 1 ? resto : 0)) / 100;
      return {
        numero: idx + 1,
        dias,
        monto,
        fecha: fecha.toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' }),
      };
    });
  };

  const handleUpdateCantidad = (index, cantidad) => {
    if (cantidad <= 0) return;

    const updatedItems = [...venta.items];
    const item = updatedItems[index];

    const stockDisponible = parseInt(item.producto?.stock_total) || parseInt(item.producto?.stock) || 0;
    const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
    const unidadesNecesarias = cantidad * factor;
    if (stockDisponible && unidadesNecesarias > stockDisponible) {
      Alert.alert(
        'Stock insuficiente',
        `Disponible: ${stockDisponible} uds. Solicitado: ${cantidad} ${item.unidad_nombre || 'Unidad'} (${unidadesNecesarias} uds)`
      );
      return;
    }

    updatedItems[index] = recalcItem({ ...item, cantidad });
    updateVentaWithItems(updatedItems);
  };

  // Permite editar el precio unitario de una línea directamente en la venta.
  // El precio es el de la presentación seleccionada (Unidad, Caja, etc.); al
  // enviar, itemsAPayload lo divide por el factor para guardarlo en unidad base.
  const handleUpdatePrecio = (index, precio) => {
    const num = Number(precio);
    if (isNaN(num) || num < 0) return;
    const updatedItems = [...venta.items];
    updatedItems[index] = recalcItem({ ...updatedItems[index], precio_unitario: num });
    updateVentaWithItems(updatedItems);
  };

  // Cambia el tipo de descuento de una línea ('porcentaje' | 'monto' | null para quitar).
  const handleUpdateLineaDescuentoTipo = (index, tipo) => {
    const updatedItems = [...venta.items];
    updatedItems[index] = recalcItem({ ...updatedItems[index], descuento_tipo: tipo });
    updateVentaWithItems(updatedItems);
  };

  const handleUpdateLineaDescuentoValor = (index, valor) => {
    const updatedItems = [...venta.items];
    const num = Number(valor) || 0;
    updatedItems[index] = recalcItem({ ...updatedItems[index], descuento_valor: num });
    updateVentaWithItems(updatedItems);
  };

  // Cambia la presentación de un item del carrito.
  // pp = { presentacion_id, nombre, factor } o null para volver a Unidad.
  // precio_unitario_efectivo = precio_venta_base * factor.
  const handleChangePresentacion = (index, pp) => {
    const updatedItems = [...venta.items];
    const item = updatedItems[index];
    const precioBase = parseFloat(item.producto?.precio_venta) || parseFloat(item.producto?.precio) || 0;
    const factor = pp ? Number(pp.factor) : 1;
    const nombre = pp ? pp.nombre : 'Unidad';
    const presId = pp ? pp.presentacion_id : null;

    // Si la presentación tiene precio propio (independiente), se usa ese;
    // si no, se calcula proporcional: precio_unidad_base * factor.
    const precioPropio = pp && pp.precio_venta != null && pp.precio_venta !== ''
      ? parseFloat(pp.precio_venta)
      : null;
    const precioPresentacion = (precioPropio != null && !isNaN(precioPropio) && precioPropio > 0)
      ? precioPropio
      : Math.round(precioBase * factor * 100) / 100;

    updatedItems[index] = recalcItem({
      ...item,
      cantidad: 1, // reset a 1 al cambiar presentación
      precio_unitario: precioPresentacion,
      factor_conversion: factor,
      unidad_nombre: nombre,
      presentacion_id: presId,
    });
    updateVentaWithItems(updatedItems);
    setPresentacionMenuIndex(null);
  };

  const updateVentaWithItems = (items, opts = {}) => {
    setVenta(prev => {
      const descTipo = opts.descuento_tipo !== undefined ? opts.descuento_tipo : prev.descuento_tipo;
      const descValor = opts.descuento_valor !== undefined ? opts.descuento_valor : prev.descuento_valor;
      const { subtotal, descuento_monto, total } = recalcVenta(items, descTipo, descValor);
      return { ...prev, items, subtotal, descuento_tipo: descTipo, descuento_valor: descValor, descuento_monto, total };
    });
  };

  const handleUpdateDescuentoGlobal = (tipo, valor) => {
    updateVentaWithItems(venta.items, {
      descuento_tipo: tipo,
      descuento_valor: Number(valor) || 0,
    });
  };
  
  // Agrega un producto al carrito en cantidad 1. Si hay loteExtra, lo asocia.
  // La cantidad se ajusta luego con el stepper del carrito.
  const addItemToCart = (producto, loteExtra = {}) => {
    if (!producto) return;
    const precio = parseFloat(producto.precio_venta) || parseFloat(producto.precio) || 0;

    if (!precio || precio <= 0) {
      Alert.alert(
        'Error',
        'Este producto no tiene un precio configurado. Por favor configúrelo antes de agregarlo a la venta.'
      );
      return;
    }

    const existingItemIndex = venta.items.findIndex(item =>
      item.producto.id === producto.id &&
      (item.lote_id || null) === (loteExtra.lote_id || null)
    );

    if (existingItemIndex !== -1) {
      const updatedItems = [...venta.items];
      const existing = updatedItems[existingItemIndex];
      updatedItems[existingItemIndex] = recalcItem({ ...existing, cantidad: existing.cantidad + 1 });
      updateVentaWithItems(updatedItems);
    } else {
      const nuevo = recalcItem({
        producto,
        cantidad: 1,
        precio_unitario: precio,
        descuento_tipo: 'porcentaje',
        descuento_valor: 0,
        presentacion_id: null,
        factor_conversion: 1,
        unidad_nombre: 'Unidad',
        ...loteExtra,
      });
      updateVentaWithItems([...venta.items, nuevo]);
    }

    setTimeout(() => setProductoSearch(''), 1000);
  };

  const toggleProductoSeleccionado = (id) => {
    const next = new Set(productosSeleccionadosIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setProductosSeleccionadosIds(next);
  };

  // Agrega al carrito todos los productos marcados en modo selección múltiple.
  // Cantidad inicial = 1 (el usuario la ajusta en el carrito).
  const handleAgregarProductosSeleccionados = () => {
    if (productosSeleccionadosIds.size === 0) return;
    const seleccionados = productos.filter(p => productosSeleccionadosIds.has(p.id));
    const sinPrecio = [];
    const updatedItems = [...venta.items];

    for (const producto of seleccionados) {
      const precio = parseFloat(producto.precio_venta) || parseFloat(producto.precio) || 0;
      if (!precio || precio <= 0) {
        sinPrecio.push(producto.nombre);
        continue;
      }
      const idx = updatedItems.findIndex(it =>
        it.producto.id === producto.id && (it.lote_id || null) === null
      );
      if (idx !== -1) {
        updatedItems[idx] = recalcItem({ ...updatedItems[idx], cantidad: updatedItems[idx].cantidad + 1 });
      } else {
        updatedItems.push(recalcItem({
          producto,
          cantidad: 1,
          precio_unitario: precio,
          descuento_tipo: 'porcentaje',
          descuento_valor: 0,
          presentacion_id: null,
          factor_conversion: 1,
          unidad_nombre: 'Unidad',
        }));
      }
    }

    updateVentaWithItems(updatedItems);
    setProductosMultiSelect(false);
    setProductosSeleccionadosIds(new Set());
    setShowProductosList(false);
    setProductoSearch('');

    if (sinPrecio.length > 0) {
      Alert.alert('Algunos no se agregaron', `Sin precio configurado: ${sinPrecio.join(', ')}`);
    }
  };
  
  // Función para formatear moneda
  const formatCurrency = (value) => {
    const numValue = parseFloat(value || 0);
    return `L. ${isNaN(numValue) ? '0.00' : numValue.toFixed(2)}`;
  };
  
  // Función para generar el HTML de la factura
  const generateFacturaHTML = (data) => {
    if (!data) return '';

    const itemsHTML = (data.items || []).map(item => `
      <tr>
        <td>${item.producto?.nombre || 'Producto sin nombre'}</td>
        <td>${item.cantidad} ${'Unidad'}</td>
        <td>${formatCurrency(item.precio_unitario)}</td>
        <td>${formatCurrency(item.subtotal)}</td>
      </tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no" />
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; margin: 0; padding: 20px; }
            .factura-container { max-width: 800px; margin: 0 auto; border: 1px solid #ddd; padding: 20px; }
            .factura-header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .factura-title { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            .factura-subtitle { font-size: 16px; color: #555; margin-bottom: 20px; }
            .factura-info { margin-bottom: 20px; }
            .factura-info div { margin-bottom: 5px; }
            .factura-label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { text-align: left; padding: 8px; border-bottom: 1px solid #ddd; }
            th { background-color: #f2f2f2; }
            .total-row { font-weight: bold; }
            .footer { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; font-size: 12px; color: #555; }
          </style>
        </head>
        <body>
          <div class="factura-container">
            <div class="factura-header">
              <div>
                <div class="factura-title">Factura de Venta</div>
                <div class="factura-subtitle">No. ${data.venta_id}</div>
              </div>
              <div>
                <div><span class="factura-label">Fecha:</span> ${data.fecha}</div>
                <div><span class="factura-label">Hora:</span> ${data.hora}</div>
              </div>
            </div>

            <div class="factura-info">
              <div class="factura-label">Cliente:</div>
              <div>${data.cliente?.nombre || 'Cliente sin nombre'}</div>
              <div>${data.cliente?.telefono || ''}</div>
            </div>

            <div class="factura-info">
              <div class="factura-label">Vendedor:</div>
              <div>${data.vendedor || 'Administrador'}</div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHTML}
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="3">Total:</td>
                  <td>${formatCurrency(data.total)}</td>
                </tr>
              </tfoot>
            </table>

            <div class="factura-info">
              <div><span class="factura-label">Método de Pago:</span> ${data.metodo_pago}</div>
              ${data.notas ? `<div><span class="factura-label">Notas:</span> ${data.notas}</div>` : ''}
            </div>

            <div class="footer">
              <p>Gracias por su compra</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // Función para generar el HTML del ticket (formato pequeño)
  const generateTicketHTML = (data) => {
    if (!data) return '';

    const itemsHTML = (data.items || []).map(item => {
      const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
      const unidad = item.unidad_nombre || 'Unidad';
      const equivHTML = factor > 1
        ? `<div class="item-details" style="font-size:10px;color:#666">= ${item.cantidad * factor} unidades (${formatCurrency(item.precio_unitario / factor)} c/u)</div>`
        : '';
      return `
      <div class="ticket-item">
        <div class="item-name">${item.producto?.nombre || 'Producto'}</div>
        ${equivHTML}
        <div class="item-details">
          <span>${item.cantidad} ${unidad} x ${formatCurrency(item.precio_unitario)}</span>
          <span class="item-total">${formatCurrency(item.subtotal)}</span>
        </div>
      </div>
    `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <style>
            @page {
              size: 80mm auto;
              margin: 0;
            }
            body {
              font-family: 'Courier New', monospace;
              margin: 0;
              padding: 10px;
              font-size: 11px;
              width: 80mm;
            }
            .ticket-container {
              width: 100%;
            }
            .ticket-header {
              text-align: center;
              margin-bottom: 10px;
              border-bottom: 1px dashed #000;
              padding-bottom: 10px;
            }
            .ticket-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 3px;
            }
            .ticket-subtitle {
              font-size: 10px;
              margin-bottom: 5px;
            }
            .ticket-info {
              margin-bottom: 10px;
              font-size: 10px;
            }
            .ticket-info-line {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2px;
            }
            .ticket-items {
              border-top: 1px dashed #000;
              border-bottom: 1px dashed #000;
              padding: 10px 0;
              margin: 10px 0;
            }
            .ticket-item {
              margin-bottom: 8px;
            }
            .item-name {
              font-weight: bold;
              margin-bottom: 2px;
            }
            .item-details {
              display: flex;
              justify-content: space-between;
              font-size: 10px;
            }
            .item-total {
              font-weight: bold;
            }
            .ticket-total {
              border-top: 1px dashed #000;
              padding-top: 10px;
              margin-top: 10px;
            }
            .total-line {
              display: flex;
              justify-content: space-between;
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .ticket-footer {
              text-align: center;
              margin-top: 15px;
              font-size: 10px;
              border-top: 1px dashed #000;
              padding-top: 10px;
            }
            .label {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="ticket-container">
            <div class="ticket-header">
              <div class="ticket-title">TICKET DE VENTA</div>
              <div class="ticket-subtitle">No. ${data.venta_id}</div>
            </div>

            <div class="ticket-info">
              <div class="ticket-info-line">
                <span class="label">Fecha:</span>
                <span>${data.fecha}</span>
              </div>
              <div class="ticket-info-line">
                <span class="label">Hora:</span>
                <span>${data.hora}</span>
              </div>
            </div>

            <div class="ticket-info">
              <div class="label">Cliente:</div>
              <div>${data.cliente?.nombre || 'Cliente'}</div>
              <div>${data.cliente?.telefono || ''}</div>
            </div>

            <div class="ticket-items">
              ${itemsHTML}
            </div>

            <div class="ticket-total">
              <div class="total-line">
                <span>TOTAL:</span>
                <span>${formatCurrency(data.total)}</span>
              </div>
              <div class="ticket-info-line">
                <span class="label">Pago:</span>
                <span>${data.metodo_pago}</span>
              </div>
              ${data.notas ? `
              <div style="margin-top: 10px;">
                <div class="label">Notas:</div>
                <div style="font-size: 9px;">${data.notas}</div>
              </div>
              ` : ''}
            </div>

            <div class="ticket-info" style="margin-top: 10px;">
              <div class="ticket-info-line">
                <span class="label">Vendedor:</span>
                <span>${data.vendedor || 'Admin'}</span>
              </div>
            </div>

            <div class="ticket-footer">
              <div>¡Gracias por su compra!</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };
  
  // Función para generar y compartir el PDF (usa generador mejorado)
  const generatePDF = async () => {
    try {
      if (!facturaData) return;
      setLoading(true);

      // Cargar config del negocio para el PDF
      let config = {};
      try {
        config = await api.getConfiguracion();
        console.log('[PDF] Config cargada:', config.nombre_negocio, '| logo_url:', config.logo_url ? `SI (${config.logo_url.substring(0, 30)}...)` : 'NO');
      } catch (e) {
        console.log('[PDF] Error al cargar config:', e.message);
      }

      // Si no hay logo en la DB, intentar usar el logo local
      if (!config.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            config.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          console.log('No se pudo cargar logo local:', logoError);
        }
      }

      const generador = printSize === 'ticket' ? genTicket : genFactura;
      const html = generador(facturaData, config, { showInternalId: false });

      const { uri } = await Print.printToFileAsync({ html });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      } else {
        Alert.alert('Error', 'La función de compartir no está disponible');
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      Alert.alert('Error', 'No se pudo generar el PDF: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMostrarPreview = () => {
    if (!venta.cliente) {
      Alert.alert('Error', 'Debe seleccionar un cliente');
      return;
    }
    if (venta.items.length === 0) {
      Alert.alert('Error', 'Debe agregar al menos un producto');
      return;
    }
    setShowPreviewModal(true);
  };

  const handleGuardarVenta = async () => {
    setShowPreviewModal(false);

    try {
      setLoading(true);
      console.log('Guardando venta...');

      // Obtener el usuario autenticado
      const userStr = await AsyncStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      if (!user || !user.id) {
        Alert.alert('Error', 'No se pudo obtener la información del usuario. Por favor, inicie sesión nuevamente.');
        setLoading(false);
        return;
      }

      // Preparar los datos para enviar al backend
      const ventaData = {
        cliente_id: venta.cliente.id,
        usuario_id: user.id,
        items: itemsAPayload(),
        descuento_tipo: venta.descuento_valor > 0 ? venta.descuento_tipo : null,
        descuento_valor: Number(venta.descuento_valor) || 0,
        metodo_pago: venta.metodo_pago,
        dias_credito: venta.metodo_pago === 'credito' ? venta.dias_credito : undefined,
        venta_directa: venta.venta_directa,
        notas: venta.notas
      };

      console.log('=== INICIANDO CREACIÓN DE VENTA ===');
      console.log('Datos de venta a enviar:', JSON.stringify(ventaData, null, 2));
      console.log('Cliente:', venta.cliente.nombre, '(ID:', venta.cliente.id, ')');
      console.log('Usuario:', user.nombre || user.email, '(ID:', user.id, ')');
      console.log('Items:', venta.items.length);
      venta.items.forEach((item, index) => {
        console.log(`  Item ${index + 1}:`, item.producto.nombre,
          `- Cantidad: ${item.cantidad}`,
          `- Precio: ${item.precio_unitario}`);
      });

      // Si venía de un borrador: actualizar + procesar el mismo registro.
      // Si no: POST normal con as_borrador=false (default).
      let response;
      if (borradorId) {
        console.log(`Procesando borrador #${borradorId}...`);
        await api.actualizarBorradorVenta(borradorId, ventaData);
        const procRes = await api.procesarBorradorVenta(borradorId, {
          venta_directa: venta.venta_directa,
          metodo_pago: venta.metodo_pago,
          dias_credito: venta.metodo_pago === 'credito' ? venta.dias_credito : 0,
        });
        response = { ...procRes, id: borradorId };
        setBorradorId(null);
      } else {
        console.log('Enviando petición al servidor...');
        response = await api.createVenta(ventaData);
      }
      console.log('Respuesta recibida:', JSON.stringify(response));

      if (response && (response.id || response.id === 0)) {
        console.log('✓ Venta creada exitosamente con ID:', response.id);
        // Marcamos para que beforeRemove no pida guardar borrador al cerrar la pantalla.
        setSalirSinPreguntar(true);
        Alert.alert('Éxito', 'Venta guardada correctamente');
        
        // Crear datos para la factura
        const facturaInfo = {
          venta_id: response.id || response.venta_id,
          id: response.id || response.venta_id,
          fecha: new Date().toISOString(),
          hora: null,
          cliente: venta.cliente,
          items: venta.items,
          subtotal: venta.subtotal,
          descuento_tipo: venta.descuento_monto > 0 ? venta.descuento_tipo : null,
          descuento_valor: venta.descuento_valor,
          descuento_monto: venta.descuento_monto,
          total: venta.total,
          metodo_pago: venta.metodo_pago,
          estado: response.estado || (venta.venta_directa ? 'completada' : 'pendiente'),
          notas: venta.notas,
          vendedor: response.vendedor || 'Administrador'
        };
        
        setFacturaData(facturaInfo);
        setShowFacturaModal(true);
        
        // Limpiar todos los campos después de guardar la venta
        setVenta({
          cliente: null,
          items: [],
          fecha: new Date().toISOString(),
          subtotal: 0,
          descuento_tipo: 'porcentaje',
          descuento_valor: 0,
          descuento_monto: 0,
          total: 0,
          metodo_pago: 'efectivo',
          dias_credito: 8,
          venta_directa: false,
          notas: '',
        });
        setProductoSearch('');
        setClienteSearch('');
        setShowClientesList(false);
        setShowProductosList(false);
      } else {
        console.error('✗ Respuesta inválida del servidor:', response);
        Alert.alert('Error', 'No se pudo guardar la venta. Intente nuevamente.');
      }
    } catch (error) {
      console.error('✗✗✗ ERROR AL CREAR VENTA ✗✗✗');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Error full response:', error.fullResponse);
      console.error('Error stack:', error.stack);

      // Construir mensaje de error más informativo
      let errorMessage = error.message || 'Error desconocido';

      if (error.details) {
        errorMessage += `\n\nDetalles: ${error.details}`;
      }

      if (error.fullResponse && error.fullResponse.error) {
        errorMessage += `\n\nInformación adicional: ${error.fullResponse.error}`;
      }

      console.error('Mostrando alerta con mensaje:', errorMessage);
      Alert.alert('Error al crear venta', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loadingText}>Cargando...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Portal>
        {/* Modal de Selección de Lote */}
        <Modal
          visible={showLoteModal}
          onDismiss={() => setShowLoteModal(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <Card style={styles.modalCard}>
            <Card.Title title="Seleccionar Lote" />
            <Card.Content>
              <Text style={styles.modalSubtitle}>Producto: {selectedProducto?.nombre}</Text>
              <Divider style={styles.divider} />

              {/* Opción sin lote */}
              <TouchableOpacity
                style={[styles.loteSelectItem, { borderColor: '#aaa', marginBottom: 8 }]}
                onPress={() => {
                  setShowLoteModal(false);
                  if (selectedProducto) addItemToCart(selectedProducto);
                }}
              >
                <Text style={{ fontWeight: 'bold', color: '#555' }}>Sin lote específico</Text>
                <Text style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Agregar sin asociar un lote</Text>
              </TouchableOpacity>

              {lotesProducto.map((lote, index) => {
                const hoy = new Date(); hoy.setHours(0,0,0,0);
                const vence = new Date(lote.fecha_vencimiento); vence.setHours(0,0,0,0);
                const dias = Math.round((vence - hoy) / (1000 * 60 * 60 * 24));
                const color = dias < 0 ? '#DC3545' : dias <= 7 ? '#FF8C00' : dias <= 30 ? '#FFC107' : '#28A745';
                return (
                  <TouchableOpacity
                    key={lote.id || index}
                    style={[styles.loteSelectItem, { borderColor: color, marginBottom: 6 }]}
                    onPress={() => {
                      setShowLoteModal(false);
                      if (selectedProducto) {
                        addItemToCart(selectedProducto, {
                          lote_id: lote.id,
                          lote_numero: lote.numero_lote,
                          lote_fecha_vencimiento: lote.fecha_vencimiento,
                        });
                      }
                    }}
                  >
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={{ fontWeight: 'bold', color: '#333' }}>Lote: {lote.numero_lote}</Text>
                      <View style={{ borderRadius: 12, paddingHorizontal: 8, paddingVertical: 3, backgroundColor: color + '22', borderWidth: 1, borderColor: color }}>
                        <Text style={{ fontSize: 11, color, fontWeight: 'bold' }}>
                          {dias < 0 ? `Vencido` : dias === 0 ? 'Vence hoy' : `${dias}d`}
                        </Text>
                      </View>
                    </View>
                    <Text style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      Vence: {lote.fecha_vencimiento ? new Date(lote.fecha_vencimiento).toLocaleDateString() : 'N/A'}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#555', marginTop: 1 }}>
                      Disponible: {lote.cantidad_actual}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </Card.Content>
            <Card.Actions>
              <Button onPress={() => setShowLoteModal(false)}>Cancelar</Button>
            </Card.Actions>
          </Card>
        </Modal>
      </Portal>

      <Card style={styles.card}>
        <Card.Title title="Datos del Cliente" />
        <Card.Content>
          {venta.cliente ? (
            <View style={[styles.clienteSeleccionado, { backgroundColor: '#e8f5e8', padding: 16, borderRadius: 8, borderWidth: 2, borderColor: '#28a745' }]}>
              <View style={styles.clienteInfo}>
                <Text style={[styles.clienteNombre, { color: '#28a745', fontWeight: 'bold' }]}>
                  ✓ Cliente Seleccionado: {venta.cliente?.nombre || 'Cliente sin nombre'}
                </Text>
                <Text style={styles.clienteTelefono}>{venta.cliente?.telefono || 'Sin teléfono'}</Text>
                <Text style={{ fontSize: 12, color: '#666' }}>ID: {venta.cliente?.id}</Text>
              </View>
              <IconButton
                icon="close"
                size={20}
                onPress={() => {
                  console.log('Deseleccionando cliente...');
                  setVenta({ ...venta, cliente: null });
                }}
              />
            </View>
          ) : (
            <View>
              <TextInput
                label="Buscar cliente por nombre, teléfono, email o dirección"
                value={clienteSearch}
                onChangeText={(text) => {
                  setClienteSearch(text);
                  setShowClientesList(text.length > 0 || clientes.length > 0);
                }}
                onFocus={handleClienteSearchFocus}
                right={
                  <TextInput.Icon 
                    icon={showClientesList ? "close" : "magnify"}
                    onPress={() => {
                      if (showClientesList) {
                        setShowClientesList(false);
                        setClienteSearch('');
                      } else {
                        setShowClientesList(true);
                      }
                    }}
                  />
                }
                style={styles.searchInput}
                placeholder="Escribe para buscar..."
              />
              
              {showClientesList && (
                <Card style={styles.dropdownCard}>
                  <ScrollView style={styles.clientesList} nestedScrollEnabled>
                    {filteredClientes.length > 0 ? (
                      <>
                        <View style={styles.dropdownHeader}>
                          <Text style={styles.dropdownHeaderText}>
                            {clienteSearch === '' 
                              ? `Selecciona un cliente (mostrando ${filteredClientes.length} de ${clientes.length})`
                              : `Resultados para "${clienteSearch}" (${filteredClientes.length})`
                            }
                          </Text>
                          <TouchableOpacity 
                            onPress={() => {
                              setShowClientesList(false);
                              setClienteSearch('');
                            }}
                            style={styles.closeDropdownButton}
                          >
                            <Text style={styles.closeDropdownText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                        {filteredClientes.map(cliente => (
                          <TouchableOpacity
                            key={cliente.id}
                            onPress={() => {
                              console.log('TOUCH DETECTED para cliente:', cliente.nombre);
                              console.log('Llamando handleSelectCliente...');
                              handleSelectCliente(cliente);
                            }}
                            style={[styles.clienteItem, { backgroundColor: '#f8f9fa' }]}
                            activeOpacity={0.5}
                          >
                            <View style={styles.clienteInfo}>
                              <Text style={styles.clienteNombre}>{cliente.nombre}</Text>
                              <View style={styles.clienteDetalles}>
                                {cliente.telefono && (
                                  <Text style={styles.clienteDetalle}>
                                    📞 {cliente.telefono}
                                  </Text>
                                )}
                                {cliente.email && (
                                  <Text style={styles.clienteDetalle}>
                                    ✉️ {cliente.email}
                                  </Text>
                                )}
                                {cliente.direccion && (
                                  <Text style={styles.clienteDetalle}>
                                    📍 {cliente.direccion}
                                  </Text>
                                )}
                              </View>
                            </View>
                          </TouchableOpacity>
                        ))}
                      </>
                    ) : (
                      <View style={styles.noResultsContainer}>
                        <Text style={styles.noResultsText}>
                          {clienteSearch === '' ? 'No hay clientes registrados' : `No se encontraron clientes con "${clienteSearch}"`}
                        </Text>
                        <Button 
                          mode="outlined" 
                          onPress={() => navigation.navigate('Clientes')}
                          style={styles.addClienteButton}
                        >
                          Agregar Nuevo Cliente
                        </Button>
                      </View>
                    )}
                  </ScrollView>
                </Card>
              )}
            </View>
          )}
        </Card.Content>
      </Card>
      
      <Card style={styles.card}>
        <Card.Title title="Productos" />
        <Card.Content>
          <TextInput
            label="Buscar producto"
            value={productoSearch}
            onChangeText={(text) => {
              setProductoSearch(text);
              setShowProductosList(text.length > 0 || filteredProductos.length > 0);
            }}
            onFocus={() => setShowProductosList(true)}
            right={
              <TextInput.Icon 
                icon={productoSearch ? "close" : "magnify"} 
                onPress={() => {
                  if (productoSearch) {
                    setProductoSearch('');
                    setShowProductosList(false);
                  }
                }}
              />
            }
            style={styles.searchInput}
          />
          
          {showProductosList && (
            <Card style={styles.dropdownCard}>
              <View style={styles.dropdownHeader}>
                <Text style={styles.dropdownHeaderText}>Productos disponibles</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <TouchableOpacity
                    onPress={() => {
                      const next = !productosMultiSelect;
                      setProductosMultiSelect(next);
                      if (!next) setProductosSeleccionadosIds(new Set());
                    }}
                    style={[styles.multiSelectToggle, productosMultiSelect && styles.multiSelectToggleActivo]}
                  >
                    <Text style={[styles.multiSelectToggleText, productosMultiSelect && styles.multiSelectToggleTextActivo]}>
                      {productosMultiSelect ? 'Multi: ON' : 'Multi: OFF'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => {
                      setShowProductosList(false);
                      setProductoSearch('');
                      setProductosMultiSelect(false);
                      setProductosSeleccionadosIds(new Set());
                    }}
                    style={styles.closeDropdownButton}
                  >
                    <Text style={styles.closeDropdownText}>✕</Text>
                  </TouchableOpacity>
                </View>
              </View>
              {filteredProductos.length > 0 ? (
                <ScrollView style={styles.dropdownScrollView} nestedScrollEnabled>
                  {filteredProductos.slice(0, 10).map(producto => {
                    const seleccionado = productosSeleccionadosIds.has(producto.id);
                    return (
                      <TouchableOpacity
                        key={producto.id}
                        onPress={() => {
                          if (productosMultiSelect) {
                            toggleProductoSeleccionado(producto.id);
                          } else {
                            handleAddProducto(producto);
                          }
                        }}
                        style={styles.productItem}
                      >
                        {productosMultiSelect && (
                          <Checkbox
                            status={seleccionado ? 'checked' : 'unchecked'}
                            onPress={() => toggleProductoSeleccionado(producto.id)}
                          />
                        )}
                        <View style={styles.productItemContent}>
                          <Text style={styles.productItemTitle}>{producto.nombre}</Text>
                          <View style={styles.productItemDetails}>
                            <Text style={styles.productItemPrice}>
                              {formatCurrency(producto.precio_venta || producto.precio || 0)}
                            </Text>
                            <Text style={styles.productItemStock}>
                              Stock: {producto.stock_total || producto.stock || 0}
                            </Text>
                          </View>
                        </View>
                        {!productosMultiSelect && (
                          <View style={styles.addButton}>
                            <Text style={styles.addButtonText}>+</Text>
                          </View>
                        )}
                      </TouchableOpacity>
                    );
                  })}
                  {filteredProductos.length > 10 && (
                    <Text style={styles.moreResultsText}>
                      Y {filteredProductos.length - 10} productos más...
                    </Text>
                  )}
                </ScrollView>
              ) : (
                <Text style={styles.noResultsText}>No se encontraron productos</Text>
              )}
              {productosMultiSelect && (
                <View style={styles.multiSelectFooter}>
                  <Text style={styles.multiSelectCount}>
                    {productosSeleccionadosIds.size} seleccionado{productosSeleccionadosIds.size === 1 ? '' : 's'}
                  </Text>
                  <Button
                    mode="contained"
                    onPress={handleAgregarProductosSeleccionados}
                    disabled={productosSeleccionadosIds.size === 0 || loading}
                    style={styles.multiSelectAddBtn}
                    compact
                  >
                    Agregar al carrito
                  </Button>
                </View>
              )}
            </Card>
          )}
          
          <Divider style={styles.divider} />
          
          {venta.items.length > 0 ? (
            <View style={styles.itemsContainer}>
              {venta.items.map((item, index) => (
                <View key={index} style={styles.itemRow}>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemNombre}>{item.producto.nombre}</Text>
                    {item.lote_numero ? <Text style={{ fontSize: 11, color: '#17A2B8' }}>Lote: {item.lote_numero}</Text> : null}
                    <View style={styles.itemPrecioRow}>
                      <Text style={styles.itemPrecio}>Precio: L.</Text>
                      {puedeEditarPrecio ? (
                        <EditablePrice
                          value={item.precio_unitario}
                          onCommit={(n) => handleUpdatePrecio(index, n)}
                          style={styles.precioInput}
                        />
                      ) : (
                        <Text style={styles.itemPrecioFijo}>{formatCurrency(item.precio_unitario).replace('L.', '').trim()}</Text>
                      )}
                      {Number(item.factor_conversion) > 1 ? (
                        <Text style={styles.itemPrecio}>
                          {` / ${item.unidad_nombre} (= ${item.cantidad * Number(item.factor_conversion)} uds)`}
                        </Text>
                      ) : null}
                    </View>

                    {/* Selector de presentación (Unidad, Caja, Docena...) */}
                    {Array.isArray(item.producto.presentaciones) && item.producto.presentaciones.length > 0 && (
                      <View style={styles.presentacionRow}>
                        <Text style={styles.presentacionLabel}>Pres:</Text>
                        <Menu
                          visible={presentacionMenuIndex === index}
                          onDismiss={() => setPresentacionMenuIndex(null)}
                          anchor={
                            <TouchableOpacity
                              style={styles.presentacionChip}
                              onPress={() => setPresentacionMenuIndex(index)}
                            >
                              <Text style={styles.presentacionChipText}>
                                {item.unidad_nombre || 'Unidad'} ▾
                              </Text>
                            </TouchableOpacity>
                          }
                        >
                          <Menu.Item
                            title="Unidad (x1)"
                            onPress={() => handleChangePresentacion(index, null)}
                          />
                          {item.producto.presentaciones.map((pp) => (
                            <Menu.Item
                              key={pp.id}
                              title={`${pp.nombre} (x${Number(pp.factor_conversion)})`}
                              onPress={() => handleChangePresentacion(index, {
                                presentacion_id: pp.presentacion_id,
                                nombre: pp.nombre,
                                factor: Number(pp.factor_conversion),
                                precio_venta: pp.precio_venta,
                              })}
                            />
                          ))}
                        </Menu>
                      </View>
                    )}

                    {/* Descuento por línea: tipo (% / L.) + valor */}
                    <View style={styles.lineaDescuentoRow}>
                      <Text style={styles.lineaDescuentoLabel}>Desc:</Text>
                      <TouchableOpacity
                        style={[styles.lineaDescTipo, item.descuento_tipo === 'porcentaje' && styles.lineaDescTipoActivo]}
                        onPress={() => handleUpdateLineaDescuentoTipo(index, 'porcentaje')}
                      >
                        <Text style={item.descuento_tipo === 'porcentaje' ? styles.lineaDescTipoTextActivo : styles.lineaDescTipoText}>%</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.lineaDescTipo, item.descuento_tipo === 'monto' && styles.lineaDescTipoActivo]}
                        onPress={() => handleUpdateLineaDescuentoTipo(index, 'monto')}
                      >
                        <Text style={item.descuento_tipo === 'monto' ? styles.lineaDescTipoTextActivo : styles.lineaDescTipoText}>L.</Text>
                      </TouchableOpacity>
                      <RNTextInput
                        value={String(item.descuento_valor || 0)}
                        onChangeText={(t) => handleUpdateLineaDescuentoValor(index, t.replace(/[^0-9.]/g, ''))}
                        keyboardType="decimal-pad"
                        style={styles.lineaDescInput}
                        selectTextOnFocus
                      />
                    </View>

                    {item.descuento_monto > 0 ? (
                      <Text style={styles.itemDescAplicado}>
                        − {formatCurrency(item.descuento_monto)}  (de {formatCurrency(item.subtotal_bruto || item.precio_unitario * item.cantidad)})
                      </Text>
                    ) : null}
                    <Text style={styles.itemSubtotal}>Subtotal: {formatCurrency(item.subtotal)}</Text>
                  </View>

                  <View style={styles.itemControls}>
                    <View style={styles.qtyStepper}>
                      <IconButton
                        icon="minus"
                        size={22}
                        mode="contained"
                        containerColor="#e3f2fd"
                        iconColor="#0066cc"
                        style={styles.qtyBtn}
                        onPress={() => handleUpdateCantidad(index, item.cantidad - 1)}
                        disabled={item.cantidad <= 1}
                      />
                      <EditableQuantity
                        value={item.cantidad}
                        onCommit={(n) => handleUpdateCantidad(index, n)}
                        style={styles.quantityText}
                      />
                      <IconButton
                        icon="plus"
                        size={22}
                        mode="contained"
                        containerColor="#e3f2fd"
                        iconColor="#0066cc"
                        style={styles.qtyBtn}
                        onPress={() => handleUpdateCantidad(index, item.cantidad + 1)}
                      />
                    </View>

                    <IconButton
                      icon="trash-can-outline"
                      size={22}
                      mode="contained"
                      containerColor="#fdecea"
                      iconColor="#dc3545"
                      style={styles.deleteBtn}
                      onPress={() => handleRemoveProducto(index)}
                    />
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.emptyItemsText}>No hay productos agregados</Text>
          )}
        </Card.Content>
      </Card>
      
      {/* Tipo de venta: directa o con proceso */}
      <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: venta.venta_directa ? '#28a745' : '#0066cc' }]}>
        <Card.Content>
          <View style={styles.ventaTipoRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.ventaTipoTitulo}>
                {venta.venta_directa ? 'Venta en Mostrador' : 'Venta con Entrega'}
              </Text>
              <Text style={styles.ventaTipoDesc}>
                {venta.venta_directa
                  ? 'Se registra como entregada al instante. Sin proceso de preparación ni ruta.'
                  : 'Pasa por: pendiente → preparación → en ruta → entregado.'}
              </Text>
            </View>
            <Switch
              value={venta.venta_directa}
              onValueChange={val => setVenta(prev => ({ ...prev, venta_directa: val }))}
              color="#28a745"
            />
          </View>
        </Card.Content>
      </Card>

      {/* Campos adicionales: Método de pago y notas */}
      <Card style={styles.card}>
        <Card.Title title="Detalles de Pago" />
        <Card.Content>
          <View style={styles.paymentDetails}>
            <Text style={styles.sectionLabel}>Método de Pago:</Text>
            <RadioButton.Group
              onValueChange={value => setVenta(prev => ({...prev, metodo_pago: value}))}
              value={venta.metodo_pago}
            >
              <View style={styles.radioGroup}>
                <View style={styles.radioOption}>
                  <RadioButton value="efectivo" />
                  <Text>Efectivo</Text>
                </View>
                <View style={styles.radioOption}>
                  <RadioButton value="tarjeta" />
                  <Text>Tarjeta</Text>
                </View>
                <View style={styles.radioOption}>
                  <RadioButton value="transferencia" />
                  <Text>Transferencia</Text>
                </View>
                <View style={styles.radioOption}>
                  <RadioButton value="credito" />
                  <Text style={{ color: '#e67e22', fontWeight: '600' }}>Crédito</Text>
                </View>
              </View>
            </RadioButton.Group>

            {venta.metodo_pago === 'credito' && (
              <View style={styles.creditoContainer}>
                <Text style={styles.creditoLabel}>Plazo de crédito:</Text>
                <View style={styles.plazosRow}>
                  {[8, 15, 22].map(dias => (
                    <View key={dias} style={styles.plazoOption}>
                      <RadioButton.Android
                        value={String(dias)}
                        status={venta.dias_credito === dias ? 'checked' : 'unchecked'}
                        onPress={() => setVenta(prev => ({ ...prev, dias_credito: dias }))}
                        color="#e67e22"
                      />
                      <Text style={venta.dias_credito === dias ? styles.plazoActivo : styles.plazoTexto}>{dias} días</Text>
                    </View>
                  ))}
                </View>

                {/* Plan de pagos calculado */}
                {venta.total > 0 && (
                  <View style={styles.planCuotasBox}>
                    <Text style={styles.planCuotasTitulo}>
                      Plan de pagos ({generarPlanCuotas(venta.total, venta.dias_credito).length} {generarPlanCuotas(venta.total, venta.dias_credito).length === 1 ? 'cuota' : 'cuotas'})
                    </Text>
                    {generarPlanCuotas(venta.total, venta.dias_credito).map(c => (
                      <View key={c.numero} style={styles.planCuotaRow}>
                        <Text style={styles.planCuotaNum}>Cuota {c.numero}</Text>
                        <Text style={styles.planCuotaFecha}>{c.fecha}</Text>
                        <Text style={styles.planCuotaMonto}>{formatCurrency(c.monto)}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
            
            <TextInput
              label="Notas"
              value={venta.notas}
              onChangeText={text => setVenta(prev => ({...prev, notas: text}))}
              multiline
              numberOfLines={2}
              style={styles.notasInput}
            />
          </View>
        </Card.Content>
      </Card>

      {/* Descuento global de la venta + desglose de totales */}
      <Card style={styles.card}>
        <Card.Title title="Descuento y Total" />
        <Card.Content>
          <View style={styles.descuentoGlobalRow}>
            <Text style={styles.sectionLabel}>Descuento global:</Text>
            <View style={styles.descuentoGlobalControls}>
              <TouchableOpacity
                style={[styles.lineaDescTipo, venta.descuento_tipo === 'porcentaje' && styles.lineaDescTipoActivo]}
                onPress={() => handleUpdateDescuentoGlobal('porcentaje', venta.descuento_valor)}
              >
                <Text style={venta.descuento_tipo === 'porcentaje' ? styles.lineaDescTipoTextActivo : styles.lineaDescTipoText}>%</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.lineaDescTipo, venta.descuento_tipo === 'monto' && styles.lineaDescTipoActivo]}
                onPress={() => handleUpdateDescuentoGlobal('monto', venta.descuento_valor)}
              >
                <Text style={venta.descuento_tipo === 'monto' ? styles.lineaDescTipoTextActivo : styles.lineaDescTipoText}>L.</Text>
              </TouchableOpacity>
              <RNTextInput
                value={String(venta.descuento_valor || 0)}
                onChangeText={(t) => handleUpdateDescuentoGlobal(venta.descuento_tipo, t.replace(/[^0-9.]/g, ''))}
                keyboardType="decimal-pad"
                style={styles.descuentoGlobalInput}
                selectTextOnFocus
                placeholder="0"
              />
            </View>
          </View>

          <View style={styles.totalesDesglose}>
            <View style={styles.totalesLine}>
              <Text style={styles.totalesLabel}>Subtotal</Text>
              <Text style={styles.totalesValor}>{formatCurrency(venta.subtotal || 0)}</Text>
            </View>
            {venta.descuento_monto > 0 ? (
              <View style={styles.totalesLine}>
                <Text style={[styles.totalesLabel, { color: '#dc3545' }]}>Descuento</Text>
                <Text style={[styles.totalesValor, { color: '#dc3545' }]}>− {formatCurrency(venta.descuento_monto)}</Text>
              </View>
            ) : null}
            <Divider style={{ marginVertical: 6 }} />
            <View style={styles.totalesLine}>
              <Text style={styles.totalesLabelTotal}>Total</Text>
              <Text style={styles.totalText}>{formatCurrency(venta.total)}</Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      <View style={styles.buttonContainer}>
        <Button mode="outlined" onPress={() => navigation.goBack()} style={styles.actionButton}>
          Cancelar
        </Button>
        <Button
          mode="contained"
          onPress={handleMostrarPreview}
          style={styles.actionButton}
          disabled={loading || !venta.cliente || venta.items.length === 0}
        >
          Revisar Pedido
        </Button>
      </View>
      
      {/* Modal de Vista Previa */}
      <Portal>
        <Modal visible={showPreviewModal} onDismiss={() => setShowPreviewModal(false)} contentContainerStyle={styles.previewModal}>
          <ScrollView style={{ maxHeight: '90%' }}>
            <Card>
              <Card.Title title="Resumen del Pedido" subtitle="Revise antes de confirmar" />
              <Card.Content>
                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Cliente</Text>
                  <Text style={styles.previewText}>{venta.cliente?.nombre || 'Sin nombre'}</Text>
                  <Text style={styles.previewSubtext}>{venta.cliente?.telefono || 'Sin teléfono'}</Text>
                  {venta.cliente?.direccion ? <Text style={styles.previewSubtext}>{venta.cliente.direccion}</Text> : null}
                </View>

                <Divider style={styles.previewDivider} />

                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Productos ({venta.items.length})</Text>
                  {venta.items.map((item, index) => {
                    const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
                    const equivBase = item.cantidad * factor;
                    return (
                      <View key={index} style={styles.previewItem}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.previewItemName}>{item.producto?.nombre || 'Producto'}</Text>
                          <Text style={styles.previewSubtext}>
                            {item.cantidad} {item.unidad_nombre || 'Unidad'} x {formatCurrency(item.precio_unitario)}
                          </Text>
                          {factor > 1 ? (
                            <Text style={[styles.previewSubtext, { fontSize: 11, color: '#888' }]}>
                              = {equivBase} unidades ({formatCurrency(item.precio_unitario / factor)} c/u)
                            </Text>
                          ) : null}
                          {item.descuento_monto > 0 ? (
                            <Text style={[styles.previewSubtext, { color: '#dc3545' }]}>
                              Desc: −{formatCurrency(item.descuento_monto)}
                              {item.descuento_tipo === 'porcentaje' ? ` (${item.descuento_valor}%)` : ''}
                            </Text>
                          ) : null}
                        </View>
                        <Text style={styles.previewItemTotal}>{formatCurrency(item.subtotal)}</Text>
                      </View>
                    );
                  })}
                </View>

                <Divider style={styles.previewDivider} />

                <View style={styles.previewSection}>
                  <Text style={styles.previewSectionTitle}>Detalles de Pago</Text>
                  <Text style={styles.previewText}>Método: {venta.metodo_pago === 'efectivo' ? 'Efectivo' : venta.metodo_pago === 'tarjeta' ? 'Tarjeta' : venta.metodo_pago === 'credito' ? `Crédito (${venta.dias_credito} días)` : 'Transferencia'}</Text>
                  <Text style={[styles.previewText, { color: venta.venta_directa ? '#28a745' : '#0066cc', fontWeight: '600' }]}>
                    {venta.venta_directa ? 'Venta en Mostrador (entregada al instante)' : 'Venta con Entrega (flujo completo)'}
                  </Text>
                  {venta.notas ? <Text style={styles.previewSubtext}>Notas: {venta.notas}</Text> : null}
                </View>

                <Divider style={styles.previewDivider} />

                <View style={styles.totalesDesglose}>
                  <View style={styles.totalesLine}>
                    <Text style={styles.totalesLabel}>Subtotal</Text>
                    <Text style={styles.totalesValor}>{formatCurrency(venta.subtotal || 0)}</Text>
                  </View>
                  {venta.descuento_monto > 0 ? (
                    <View style={styles.totalesLine}>
                      <Text style={[styles.totalesLabel, { color: '#dc3545' }]}>Descuento</Text>
                      <Text style={[styles.totalesValor, { color: '#dc3545' }]}>− {formatCurrency(venta.descuento_monto)}</Text>
                    </View>
                  ) : null}
                </View>

                <View style={styles.previewTotalRow}>
                  <Text style={styles.previewTotalLabel}>TOTAL</Text>
                  <Text style={styles.previewTotalValue}>{formatCurrency(venta.total)}</Text>
                </View>
              </Card.Content>
              <Card.Actions style={styles.previewActions}>
                <Button onPress={() => setShowPreviewModal(false)} style={{ flex: 1 }}>Editar</Button>
                <Button
                  mode="contained"
                  onPress={handleGuardarVenta}
                  loading={loading}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  Confirmar Venta
                </Button>
              </Card.Actions>
            </Card>
          </ScrollView>
        </Modal>
      </Portal>

      {/* Modal de Factura */}
      <Portal>
        <Modal visible={showFacturaModal} onDismiss={() => setShowFacturaModal(false)} contentContainerStyle={styles.facturaModal}>
          <Card style={styles.facturaCard}>
            <Card.Title title="Factura de Venta" subtitle={facturaData ? `No. ${facturaData.venta_id}` : ''} titleStyle={styles.facturaCardTitle} />
            <ScrollView style={styles.facturaScroll} contentContainerStyle={styles.facturaScrollContent}>
              {facturaData && (
                <>
                  <View style={styles.facturaHeader}>
                    <View style={styles.facturaHeaderCol}>
                      <Text style={styles.facturaLabel}>Fecha</Text>
                      <Text style={styles.facturaValue}>{new Date(facturaData.fecha).toLocaleDateString('es-HN')}</Text>
                    </View>
                    <View style={styles.facturaHeaderCol}>
                      <Text style={styles.facturaLabel}>Hora</Text>
                      <Text style={styles.facturaValue}>{new Date(facturaData.fecha).toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' })}</Text>
                    </View>
                  </View>

                  <Divider style={styles.facturaDivider} />

                  <View style={styles.facturaClienteInfo}>
                    <Text style={styles.facturaLabel}>Cliente</Text>
                    <Text style={styles.facturaValue} numberOfLines={2}>{facturaData.cliente?.nombre || 'Cliente sin nombre'}</Text>
                    <Text style={styles.facturaValueSecundario}>{facturaData.cliente?.telefono || 'Sin teléfono'}</Text>
                  </View>

                  <Divider style={styles.facturaDivider} />

                  <Text style={styles.facturaSubtitle}>Productos</Text>
                  {facturaData.items.map((item, index) => (
                    <View key={index} style={styles.facturaItem}>
                      <View style={styles.facturaItemDetails}>
                        <Text style={styles.facturaItemName} numberOfLines={2}>{item.producto?.nombre || 'Producto sin nombre'}</Text>
                        <Text style={styles.facturaItemSub}>{item.cantidad} x {formatCurrency(item.precio_unitario)}</Text>
                      </View>
                      <Text style={styles.facturaItemTotal} numberOfLines={1}>{formatCurrency(item.subtotal)}</Text>
                    </View>
                  ))}

                  <Divider style={styles.facturaDivider} />

                  <View style={styles.facturaTotalRow}>
                    <Text style={styles.facturaTotal}>Total</Text>
                    <Text style={styles.facturaTotalAmount} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{formatCurrency(facturaData.total)}</Text>
                  </View>

                  <View style={styles.facturaInfoRow}>
                    <Text style={styles.facturaLabel}>Método de Pago</Text>
                    <Text style={styles.facturaValue}>{facturaData.metodo_pago}</Text>
                  </View>

                  <View style={styles.facturaInfoRow}>
                    <Text style={styles.facturaLabel}>Vendedor</Text>
                    <Text style={styles.facturaValue} numberOfLines={1}>{facturaData.vendedor || 'Administrador'}</Text>
                  </View>

                  {facturaData.notas && (
                    <View style={styles.facturaInfoRow}>
                      <Text style={styles.facturaLabel}>Notas</Text>
                      <Text style={styles.facturaValue}>{facturaData.notas}</Text>
                    </View>
                  )}

                  <Divider style={styles.facturaDivider} />

                  <View style={styles.printSizeSelector}>
                    <Text style={styles.facturaLabel}>Tamaño de Impresión</Text>
                    <RadioButton.Group
                      onValueChange={value => setPrintSize(value)}
                      value={printSize}
                    >
                      <TouchableOpacity style={styles.radioOption} onPress={() => setPrintSize('factura')}>
                        <RadioButton value="factura" />
                        <Text style={styles.radioOptionLabel}>Factura (Tamaño Carta)</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.radioOption} onPress={() => setPrintSize('ticket')}>
                        <RadioButton value="ticket" />
                        <Text style={styles.radioOptionLabel}>Ticket (80mm)</Text>
                      </TouchableOpacity>
                    </RadioButton.Group>
                  </View>
                </>
              )}
            </ScrollView>
            <Card.Actions style={styles.facturaActions}>
              <Button onPress={() => setShowFacturaModal(false)}>Cerrar</Button>
              <Button
                mode="contained"
                onPress={generatePDF}
                loading={loading}
                disabled={loading}
              >
                Generar PDF
              </Button>
            </Card.Actions>
          </Card>
        </Modal>
      </Portal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  card: {
    marginBottom: 16,
    borderRadius: 8,
    elevation: 2,
  },
  paymentDetails: {
    marginVertical: 10,
  },
  radioGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 10,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
  },
  ventaTipoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  ventaTipoTitulo: {
    fontSize: 15,
    fontWeight: '700',
    color: '#333',
    marginBottom: 3,
  },
  ventaTipoDesc: {
    fontSize: 12,
    color: '#666',
    lineHeight: 17,
  },
  creditoContainer: {
    marginTop: 10,
    padding: 10,
    backgroundColor: '#fff8f0',
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#e67e22',
  },
  creditoLabel: {
    fontWeight: '600',
    color: '#e67e22',
    marginBottom: 6,
  },
  plazosRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  plazoOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  plazoTexto: {
    color: '#555',
  },
  plazoActivo: {
    color: '#e67e22',
    fontWeight: '700',
  },
  planCuotasBox: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0d9bd',
  },
  planCuotasTitulo: {
    fontSize: 13,
    fontWeight: '700',
    color: '#5a4030',
    marginBottom: 6,
  },
  planCuotaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  planCuotaNum: {
    width: 70,
    fontSize: 13,
    color: '#666',
    fontWeight: '600',
  },
  planCuotaFecha: {
    flex: 1,
    fontSize: 13,
    color: '#444',
  },
  planCuotaMonto: {
    fontSize: 13,
    fontWeight: '700',
    color: '#e67e22',
  },
  notasInput: {
    marginTop: 10,
  },
  sectionLabel: {
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 5,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 16,
  },
  button: {
    flex: 1,
    marginHorizontal: 5,
  },
  totalText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0066cc',
    paddingHorizontal: 16,
  },
  presentacionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 2,
  },
  presentacionLabel: {
    fontSize: 12,
    color: '#666',
    marginRight: 6,
  },
  presentacionChip: {
    borderWidth: 1,
    borderColor: '#0066cc',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    backgroundColor: '#E7F1FF',
  },
  presentacionChipText: {
    fontSize: 12,
    color: '#0066cc',
    fontWeight: '600',
  },
  lineaDescuentoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 2,
  },
  lineaDescuentoLabel: {
    fontSize: 12,
    color: '#666',
    marginRight: 6,
  },
  lineaDescTipo: {
    borderWidth: 1,
    borderColor: '#ccc',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 4,
    backgroundColor: '#fff',
  },
  lineaDescTipoActivo: {
    backgroundColor: '#0066cc',
    borderColor: '#0066cc',
  },
  lineaDescTipoText: {
    color: '#333',
    fontSize: 12,
    fontWeight: '600',
  },
  lineaDescTipoTextActivo: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  lineaDescInput: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    minWidth: 56,
    fontSize: 12,
    color: '#333',
    backgroundColor: '#fff',
    marginLeft: 4,
  },
  itemDescAplicado: {
    fontSize: 12,
    color: '#dc3545',
    marginTop: 2,
  },
  descuentoGlobalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  descuentoGlobalControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  descuentoGlobalInput: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    minWidth: 80,
    fontSize: 14,
    color: '#333',
    backgroundColor: '#fff',
    marginLeft: 6,
  },
  totalesDesglose: {
    marginTop: 4,
  },
  totalesLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  totalesLabel: {
    fontSize: 14,
    color: '#555',
  },
  totalesLabelTotal: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  totalesValor: {
    fontSize: 14,
    color: '#333',
  },
  facturaModal: {
    width: '92%',
    maxHeight: Dimensions.get('window').height * 0.88,
    alignSelf: 'center',
    backgroundColor: 'transparent',
  },
  facturaCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
  },
  facturaCardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  facturaScroll: {
    maxHeight: Dimensions.get('window').height * 0.66,
  },
  facturaScrollContent: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 12,
  },
  facturaHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    gap: 12,
  },
  facturaHeaderCol: {
    flex: 1,
  },
  facturaLabel: {
    fontWeight: 'bold',
    fontSize: 12,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 2,
  },
  facturaValue: {
    fontSize: 14,
    color: '#222',
  },
  facturaValueSecundario: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },
  facturaDivider: {
    marginVertical: 8,
  },
  facturaClienteInfo: {
    marginVertical: 4,
  },
  facturaSubtitle: {
    fontSize: 15,
    fontWeight: 'bold',
    marginTop: 4,
    marginBottom: 8,
    color: '#333',
  },
  facturaItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginVertical: 4,
    gap: 8,
  },
  facturaItemDetails: {
    flex: 1,
  },
  facturaItemName: {
    fontWeight: '600',
    fontSize: 14,
    color: '#222',
  },
  facturaItemSub: {
    fontSize: 12,
    color: '#777',
    marginTop: 2,
  },
  facturaItemTotal: {
    minWidth: 90,
    textAlign: 'right',
    fontWeight: 'bold',
    fontSize: 14,
    color: '#222',
  },
  facturaTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 8,
    paddingVertical: 6,
  },
  facturaTotal: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  facturaTotalAmount: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0066cc',
    maxWidth: '60%',
  },
  facturaInfoRow: {
    marginVertical: 4,
  },
  printSizeSelector: {
    marginTop: 8,
  },
  radioOptionLabel: {
    fontSize: 14,
    color: '#333',
    marginLeft: 4,
  },
  facturaActions: {
    justifyContent: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  modalContainer: {
    padding: 20,
    margin: 20,
  },
  modalCard: {
    padding: 10,
    borderRadius: 10,
  },
  modalSubtitle: {
    fontSize: 16,
    marginBottom: 10,
    fontWeight: 'bold',
  },
  multiToggleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 6,
    paddingRight: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    marginBottom: 8,
  },
  multiToggleText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0066cc',
  },
  multiToggleHint: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
    paddingRight: 12,
  },
  unidadItem: {
    marginVertical: 5,
    borderRadius: 6,
    paddingVertical: 4,
  },
  unidadRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  unidadInfo: {
    marginLeft: 10,
  },
  unidadNombre: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
  },
  searchInput: {
    backgroundColor: 'white',
    marginBottom: 8,
  },
  dropdownCard: {
    marginBottom: 8,
    maxHeight: 300,
    borderRadius: 8,
    elevation: 4,
  },
  dropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#f8f9fa',
  },
  dropdownHeaderText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#666',
  },
  closeDropdownButton: {
    padding: 4,
    borderRadius: 12,
    backgroundColor: '#dc3545',
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  multiSelectToggle: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#e9ecef',
    marginRight: 8,
  },
  multiSelectToggleActivo: {
    backgroundColor: '#0d6efd',
  },
  multiSelectToggleText: {
    fontSize: 11,
    color: '#495057',
    fontWeight: 'bold',
  },
  multiSelectToggleTextActivo: {
    color: 'white',
  },
  multiSelectFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    backgroundColor: '#f8f9fa',
  },
  multiSelectCount: {
    fontSize: 12,
    color: '#495057',
    fontWeight: 'bold',
  },
  multiSelectAddBtn: {
    backgroundColor: '#28a745',
  },
  closeDropdownText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  dropdownScrollView: {
    maxHeight: 240,
  },
  productItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  productItemContent: {
    flex: 1,
  },
  productItemTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  productItemDescription: {
    fontSize: 14,
    color: '#666',
  },
  productItemDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  productItemPrice: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#28a745',
  },
  productItemStock: {
    fontSize: 12,
    color: '#666',
    marginLeft: 10,
  },
  addButton: {
    backgroundColor: '#28a745',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  addButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  moreResultsText: {
    textAlign: 'center',
    padding: 12,
    color: '#666',
    fontSize: 12,
    fontStyle: 'italic',
  },
  dropdownItem: {
    paddingVertical: 4,
  },
  noResultsText: {
    textAlign: 'center',
    padding: 16,
    color: '#666',
  },
  clientesList: {
    maxHeight: 280,
  },
  clienteItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  clienteDetalles: {
    flexDirection: 'column',
    marginTop: 4,
  },
  clienteDetalle: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  noResultsContainer: {
    padding: 20,
    alignItems: 'center',
  },
  addClienteButton: {
    marginTop: 10,
  },
  clienteSeleccionado: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  clienteInfo: {
    flex: 1,
  },
  clienteNombre: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  clienteTelefono: {
    fontSize: 14,
    color: '#666',
  },
  divider: {
    marginVertical: 16,
  },
  itemsContainer: {
    marginTop: 8,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  itemNombre: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  itemPrecio: {
    fontSize: 14,
    color: '#555',
  },
  itemPrecioFijo: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#111',
    marginLeft: 4,
  },
  itemPrecioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginVertical: 2,
  },
  precioInput: {
    width: 80,
    flexGrow: 0,
    flexShrink: 0,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    paddingVertical: 3,
    paddingHorizontal: 8,
    marginHorizontal: 4,
    fontSize: 14,
    color: '#111',
    backgroundColor: '#fff',
    textAlign: 'right',
  },
  itemUnidad: {
    fontSize: 14,
    color: '#555',
    fontStyle: 'italic',
  },
  itemSubtotal: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  itemControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 0,
    flexShrink: 0,
  },
  qtyStepper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#bcd9f7',
    borderRadius: 24,
    backgroundColor: '#f4f9ff',
    paddingHorizontal: 2,
  },
  qtyBtn: {
    margin: 0,
  },
  quantityText: {
    fontSize: 15,
    fontWeight: 'bold',
    width: 44,
    flexGrow: 0,
    flexShrink: 0,
    textAlign: 'center',
    paddingVertical: 4,
    paddingHorizontal: 4,
    color: '#0d3a66',
    backgroundColor: 'transparent',
  },
  deleteBtn: {
    margin: 0,
    marginLeft: 16,
    borderWidth: 1,
    borderColor: '#f5c6cb',
  },
  subtotalText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#28a745',
    marginRight: 8,
  },
  emptyItemsText: {
    textAlign: 'center',
    padding: 16,
    color: '#666',
  },
  totalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  totalAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#28a745',
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 16,
  },
  actionButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  previewModal: {
    margin: 16,
    maxHeight: '90%',
  },
  previewSection: {
    marginVertical: 8,
  },
  previewSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  previewText: {
    fontSize: 15,
    color: '#333',
    fontWeight: '600',
  },
  previewSubtext: {
    fontSize: 13,
    color: '#888',
    marginTop: 2,
  },
  previewDivider: {
    marginVertical: 10,
  },
  previewItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  previewItemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  previewItemTotal: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0066cc',
  },
  previewTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    backgroundColor: '#f0f7ff',
    paddingHorizontal: 12,
    borderRadius: 8,
    marginTop: 4,
  },
  previewTotalLabel: {
    fontSize: 18,
    fontWeight: '700',
    color: '#333',
  },
  previewTotalValue: {
    fontSize: 20,
    fontWeight: '800',
    color: '#0066cc',
  },
  previewActions: {
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  loteSelectItem: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    backgroundColor: '#fafafa',
  },
});

export default NuevaVentaScreen;
